import { Dropdown, Flex, Tag, Tooltip, Typography } from "antd"
import dayjs from "dayjs"

import ButtonComponent from "../../../../../../Components/Button"
import Icon from "../../../../../../Icon"
import { formatCurrency } from "../../../../../../Utils/utils"
import {
  referralColorMap,
  referralTagBgColorMap,
  referralTextMap
} from "../../../../utils/constants"

const { Text } = Typography

const onUserCell = (record) => {
  return {
    rowSpan: record?.userRowSpan
  }
}

const onReferralCell = (record) => {
  return {
    rowSpan: record?.referralRowSpan
  }
}

const getStatusTag = (status, theme) => {
  const bgColor = theme === "dark" ? referralColorMap[status] : referralTagBgColorMap[status]
  const textColor = theme === "dark" ? referralTagBgColorMap[status] : referralColorMap[status]
  return (
    <Tag key={status} style={{ background: bgColor }} className={`newTagStyle referral`}>
      <Flex gap={8} align="center">
        <Text style={{ color: textColor }}>{status ? referralTextMap[status] : "—"}</Text>
      </Flex>
    </Tag>
  )
}

export const getColumns = ({ data, onSelectRow, theme }) => {
  return {
    affiliate_full_name: {
      key: "user_name",
      dataIndex: "user_name",
      title: "Affiliate",
      className: "align-top",
      onCell: onUserCell,
      minWidth: 100,
      render: (_, record) => (
        <div>
          <ButtonComponent type="link" onClick={() => onSelectRow(record, "user_name")}>
            {_}
          </ButtonComponent>
        </div>
      ),
      fixed: "left"
    },
    affiliate_email: {
      key: "user_email",
      dataIndex: "user_email",
      title: "Affiliate email",
      className: "align-top",
      minWidth: 140,
      render: (_, record) => {
        return (
          <Flex align="center" style={{ height: "32px" }}>
            <Text>{_}</Text>
          </Flex>
        )
      },
      onCell: onUserCell
    },
    categories: {
      key: "categories",
      title: "Type",
      width: 200,
      dataIndex: "categories",
      className: "align-top",
      render: (_, record) => {
        return <Text>{_?.length ? _?.join(", ") : "—"}</Text>
      },
      onCell: onUserCell
    },
    referral_full_name: {
      key: "user_name",
      dataIndex: "user_name",
      title: "Invited referral",
      render: (_, { referral }) => {
        const userName = referral?.user_name
        return (
          <Flex align="center" style={{ height: "32px" }}>
            <Text>{userName || "—"}</Text>
          </Flex>
        )
      },
      className: "align-top",
      minWidth: 150,
      onCell: onReferralCell
    },
    referral_email: {
      key: "user_email",
      dataIndex: "user_email",
      title: "Invited referral email",
      render: (_, { referral }) => {
        const userEmail = referral?.user_email
        return (
          <Flex align="center" style={{ height: "32px" }}>
            <Text>{userEmail || "—"}</Text>
          </Flex>
        )
      },
      className: "align-top",
      minWidth: 200,
      onCell: onReferralCell
    },
    referral_status: {
      key: "status",
      dataIndex: "status",
      title: "Referral status",
      width: 128,
      render: (_, record) => (
        <Flex align="center" style={{ height: "32px" }}>
          {getStatusTag(record?.referral?.status, theme)}
        </Flex>
      ),
      className: "align-top",
      onCell: onReferralCell
    },
    business_account_created: {
      key: "date",
      dataIndex: "date",
      title: "Date",
      width: 128,
      render: (_, { business_account }) => {
        const date = business_account?.created
        const formatted = dayjs(date).format("DD/MM/YYYY")
        return <Text>{business_account ? formatted : "—"}</Text>
      }
    },
    business_account_name: {
      key: "referral_ba",
      title: "Referral BA",
      minWidth: 130,
      render: (_, record) => {
        const business_account = record?.business_account
        return business_account ? (
          <Flex gap={4}>
            <Text
              copyable={{
                tooltips: false,
                text: business_account.name
              }}
            />
            <Dropdown
              destroyPopupOnHide={true}
              menu={{
                items: [
                  { key: "business_account", label: "Business account" },
                  { key: "dashboard", label: "Users view" }
                ],
                onClick: ({ key, domEvent }) => {
                  domEvent?.stopPropagation()
                  onSelectRow(record, key)
                }
              }}
            >
              <Flex gap={8} align="center">
                <Text className="blue">{business_account.name}</Text>
                <Icon type="chevron_down_small" style={{ marginLeft: -4 }} />
              </Flex>
            </Dropdown>
          </Flex>
        ) : (
          "—"
        )
      }
    },
    business_account_status: {
      key: "ba_status",
      dataIndex: "ba_status",
      title: "BA status",
      minWidth: 130,
      render: (_, record) => getStatusTag(record?.business_account?.status, theme)
    },
    reward: {
      key: "reward",
      dataIndex: "reward",
      title: "Reward",
      width: 128,
      render: (_, record) => {
        const reward = record?.business_account?.reward_amount
        return (
          <ButtonComponent
            type="link"
            onClick={() => onSelectRow(record, "reward")}
            disabled={!record?.business_account}
          >
            {record?.business_account ? formatCurrency(reward / 100) : "—"}
          </ButtonComponent>
        )
      }
    },
    alerts: {
      key: "alerts",
      dataIndex: "alerts",
      title: "Alerts",
      width: 128,
      render: (_, record) => {
        const alerts = record?.business_account?.alerts
        return (
          <ButtonComponent
            type="link"
            onClick={() => onSelectRow(record, "alerts")}
            disabled={!record?.business_account}
          >
            {record?.business_account ? alerts?.toLocaleString() : "—"}
          </ButtonComponent>
        )
      }
    }
  }
}

export const getActionColumn = ({ onSelectRow }) => {
  const handleAction = (e, data, type) => {
    e.preventDefault()
    e.stopPropagation()
    onSelectRow(data, type)
  }
  return {
    key: "action",
    title: "",
    width: 64,
    fixed: "right",
    render: (_, record) => (
      <Flex gap={4} data-tour={record.id === 4 ? "alert-details" : null}>
        <Tooltip title="View Details">
          <ButtonComponent
            type="link"
            shape="circle"
            disabled={!record?.business_account}
            icon={<Icon type={"layout_align_right"} />}
            onClick={(e) => handleAction(e, record, "referral_ba_details")}
            className="flex center"
          />
        </Tooltip>
      </Flex>
    )
  }
}
